import { FC } from "react";

import { Stack } from "@mui/material";
import { CommunicationPreferenceFormType, Locales } from "@simplyk/common";

import { CommunicationPreferencesFormContent } from "./CommunicationPreferencesFormContent";
import { useCommunicationPreferencesForm } from "./helpers/useCommunicationPreferencesForm";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Lists } from "@/icons/outlined";

interface EditCommunicationPreferencesModalProps {
  isOpen: boolean;
  formId: string;
  formType: CommunicationPreferenceFormType;
  formLocale: Locales;
  onClose: () => void;
  onSaved?: () => void;
}

export const EditCommunicationPreferencesModal: FC<EditCommunicationPreferencesModalProps> = ({
  isOpen,
  formId,
  formType,
  formLocale,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslate();

  const {
    options,
    selectedContactListIds,
    setSelectedContactListIds,
    questionTitle,
    defaultQuestionTitle,
    setQuestionTitle,
    canManageOptions,
    isLoading,
    isSaving,
    canSave,
    handleCreateOption,
    handleRenameOption,
    handleDeleteOption,
    handleSave,
  } = useCommunicationPreferencesForm({
    formId,
    formType,
    formLocale,
    enabled: isOpen,
    onSaved: () => {
      onSaved?.();
      onClose();
    },
  });

  return (
    <DashboardDialog open={isOpen} onClose={onClose} maxWidth="md">
      <DashboardDialogBody
        slots={{
          header: (
            <DashboardDialogHeader
              sx={{ pb: 0 }}
              icon={<Lists />}
              title={t("dashboard", "communicationPreferences.questionTitle")}
            />
          ),
          content: (
            <DashboardDialogContent sx={{ pt: 0 }}>
              <Stack spacing={2}>
                <DashboardTypography variant="body1" vibe="text-neutral-moderate">
                  {t("dashboard", "communicationPreferences.modalDescription")}
                </DashboardTypography>
                <CommunicationPreferencesFormContent
                  options={options}
                  selectedContactListIds={selectedContactListIds}
                  questionTitle={questionTitle}
                  defaultQuestionTitle={defaultQuestionTitle}
                  isLoading={isLoading}
                  canManageOptions={canManageOptions}
                  formType={formType}
                  onSelectedContactListIdsChange={setSelectedContactListIds}
                  onQuestionTitleChange={setQuestionTitle}
                  onCreateOption={handleCreateOption}
                  onRenameOption={handleRenameOption}
                  onDeleteOption={handleDeleteOption}
                />
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton
                onClick={handleSave}
                isLoading={isSaving}
                disabled={!canSave}
                data-test="communication-preferences-save-button"
              >
                {t("dashboard", "stepQuestionsV4.builder.saveQuestion")}
              </DashboardButton>
              <DashboardButton onClick={onClose}>{t("dashboard", "common.cancel")}</DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
